import React from 'react'
import { Typography } from '@mui/material'
import { Confirm, Form, useNotify, useTranslate } from 'react-admin'
import { includes, isNil, map, replace, split } from 'lodash/fp'
import log from 'loglevel'

import {
  doAccountsUpload,
  doCognitoUpload,
  doOrganizationsUpload,
  doUpload
} from './functions'
import { type Partner } from '@mzc-watchover/adminapp-appsync'

log.setLevel(log.levels.DEBUG)

export const UploadDialog: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
  partner: Partial<Partner> | undefined
  permissions: string[]
}> = ({ open, setOpen, partner, permissions }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const upload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (isNil(event.target.files) || isNil(event.target.files[0])) return
    notify('resources.upload.notification.uploading')
    const reader = new FileReader()
    reader.onload = () => {
      if (isNil(reader.result)) return
      const file = replace(
        /^data:application\/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,/,
        ''
      )(reader.result as string)
      doUpload(
        file,
        partner,
        includes('noStop')(permissions),
        includes('editCriterion')(permissions),
        includes('changeContact')(permissions)
      )
        .then((_status) => {})
        .catch(log.error)
        .finally(() => {
          setOpen(false)
        })
    }
    reader.readAsDataURL(event.target.files[0])
    event.target.value = ''
  }
  const onClose = (): void => {
    setOpen(false)
  }
  const onConfirm = (): void => {
    setOpen(false)
    if (!isNil(inputRef.current)) {
      inputRef.current.click()
    }
  }
  return (
    <Form>
      <Confirm
        isOpen={open}
        title={false}
        content={map((item: string) => (
          <Typography variant="body1" key={item}>
            {item}
            <br />
          </Typography>
        ))(split('\n')(translate('resources.upload.dialog.content')))}
        onClose={onClose}
        onConfirm={onConfirm}
        cancel="resources.upload.dialog.buttons.cancel"
        confirm="resources.upload.dialog.buttons.confirm"
      />
      <input
        hidden
        ref={inputRef}
        type="file"
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={upload}
      />
    </Form>
  )
}
export const CognitoUploadDialog: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
}> = ({ open, setOpen }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const upload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (isNil(event.target.files) || isNil(event.target.files[0])) return
    notify('resources.cognito.upload.notification.uploading')
    const reader = new FileReader()
    reader.onload = () => {
      if (isNil(reader.result)) return
      const file = replace(
        /^data:application\/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,/,
        ''
      )(reader.result as string)
      doCognitoUpload(file)
        .then((_status) => {})
        .catch(log.error)
        .finally(() => {
          setOpen(false)
        })
    }
    reader.readAsDataURL(event.target.files[0])
    event.target.value = ''
  }
  const onClose = (): void => {
    setOpen(false)
  }
  const onConfirm = (): void => {
    if (!isNil(inputRef.current)) {
      inputRef.current.click()
    }
  }
  return (
    <Form>
      <Confirm
        isOpen={open}
        title={false}
        content={map((item: string) => (
          <Typography variant="body1" key={item}>
            {item}
            <br />
          </Typography>
        ))(split('\n')(translate('resources.cognito.upload.dialog.content')))}
        onClose={onClose}
        onConfirm={onConfirm}
        cancel="resources.cognito.upload.dialog.buttons.cancel"
        confirm="resources.cognito.upload.dialog.buttons.confirm"
      />
      <input
        hidden
        ref={inputRef}
        type="file"
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={upload}
      />
    </Form>
  )
}
export const OrganizationsUploadDialog: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
  partner: Partial<Partner> | undefined
  permissions: string[]
}> = ({ open, setOpen, partner }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const upload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (isNil(event.target.files) || isNil(event.target.files[0])) return
    notify('resources.editOrganizations.upload.notification.uploading')
    const reader = new FileReader()
    reader.onload = () => {
      if (isNil(reader.result)) return
      const file = replace(
        /^data:application\/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,/,
        ''
      )(reader.result as string)
      doOrganizationsUpload(file, partner)
        .then((_status) => {})
        .catch(log.error)
        .finally(() => {
          setOpen(false)
        })
    }
    reader.readAsDataURL(event.target.files[0])
    event.target.value = ''
  }
  const onClose = (): void => {
    setOpen(false)
  }
  const onConfirm = (): void => {
    setOpen(false)
    if (!isNil(inputRef.current)) {
      inputRef.current.click()
    }
  }
  return (
    <Form>
      <Confirm
        isOpen={open}
        title={false}
        content={map((item: string) => (
          <Typography variant="body1" key={item}>
            {item}
            <br />
          </Typography>
        ))(
          split('\n')(
            translate('resources.editOrganizations.upload.dialog.content')
          )
        )}
        onClose={onClose}
        onConfirm={onConfirm}
        cancel="resources.editOrganizations.upload.dialog.buttons.cancel"
        confirm="resources.editOrganizations.upload.dialog.buttons.confirm"
      />
      <input
        hidden
        ref={inputRef}
        type="file"
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={upload}
      />
    </Form>
  )
}
export const AccountsUploadDialog: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
  partner: Partial<Partner> | undefined
  permissions: string[]
}> = ({ open, setOpen, partner }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const upload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (isNil(event.target.files) || isNil(event.target.files[0])) return
    notify('resources.editAccounts.upload.notification.uploading')
    const reader = new FileReader()
    reader.onload = () => {
      if (isNil(reader.result)) return
      const file = replace(
        /^data:application\/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,/,
        ''
      )(reader.result as string)
      doAccountsUpload(file, partner)
        .then((_status) => {})
        .catch(log.error)
        .finally(() => {
          setOpen(false)
        })
    }
    reader.readAsDataURL(event.target.files[0])
    event.target.value = ''
  }
  const onClose = (): void => {
    setOpen(false)
  }
  const onConfirm = (): void => {
    setOpen(false)
    if (!isNil(inputRef.current)) {
      inputRef.current.click()
    }
  }
  return (
    <Form>
      <Confirm
        isOpen={open}
        title={false}
        content={map((item: string) => (
          <Typography variant="body1" key={item}>
            {item}
            <br />
          </Typography>
        ))(
          split('\n')(translate('resources.editAccounts.upload.dialog.content'))
        )}
        onClose={onClose}
        onConfirm={onConfirm}
        cancel="resources.editAccounts.upload.dialog.buttons.cancel"
        confirm="resources.editAccounts.upload.dialog.buttons.confirm"
      />
      <input
        hidden
        ref={inputRef}
        type="file"
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={upload}
      />
    </Form>
  )
}
